<script setup>
import { onMounted } from 'vue'
import ProductModelStage from '../components/home/ProductModelStage.vue'
import SiteFooter from '../components/layout/SiteFooter.vue'
import SiteHeader from '../components/layout/SiteHeader.vue'
import { appConfig } from '../utils/config'
import { openStore, redirectToAvailableStoreIfNeeded } from '../utils/download'

const features = [
  {
    title: '生动表达',
    description: '让每一次互动都充满生命力',
    icon: 'star',
  },
  {
    title: '自然对话',
    description: '像朋友一样流畅沟通',
    icon: 'chat',
  },
  {
    title: '随时连接',
    description: '稳定可靠，始终在线',
    icon: 'signal',
  },
  {
    title: '响应迅速',
    description: '毫秒级反馈，毫无延迟',
    icon: 'bolt',
  },
]

onMounted(() => {
  redirectToAvailableStoreIfNeeded()
})
</script>

<template>
  <div class="download-page">
    <SiteHeader />

    <main id="main-content" class="download-main">
      <section class="download-hero">
        <div class="download-hero__inner">
          <div class="download-hero__copy">
            <h1 class="download-headline">
              <span>让生活更有趣</span>
              <span>让智能更贴近人们</span>
              <span class="download-headline__highlight">让用户更专注自己</span>
            </h1>

            <div class="download-panel">
              <div class="download-panel__header">
                <div class="download-panel__label">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M12 3v10m0 0 4-4m-4 4-4-4M5 19h14"
                      fill="none"
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.8"
                    />
                  </svg>
                  <span>扫码下载 App</span>
                </div>
                <p class="download-panel__meta">
                  App Store / 鸿蒙已上线 · Android 扫码下载安装包 · 华为应用市场也可下载
                </p>
              </div>

              <div class="download-panel__stores">
                <div class="download-store">
                  <div class="download-panel__qr">
                    <img
                      v-if="appConfig.downloadQrImageUrls.ios"
                      :src="appConfig.downloadQrImageUrls.ios"
                      alt="App Store 下载二维码"
                      class="download-panel__qr-image"
                    />
                  </div>
                  <button
                    type="button"
                    class="store-button"
                    @click="openStore('ios')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M16.2 12.6c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.4 1.1 8.5.7 1 1.6 2.2 2.7 2.1 1.1-.1 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.6Zm-2-5.9c.6-.7 1-1.7.9-2.7-1 .1-2.1.6-2.7 1.4-.6.7-1.1 1.7-.9 2.7 1 0 2-.5 2.7-1.4Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>App Store</span>
                  </button>
                </div>

                <div class="download-store">
                  <div class="download-panel__qr">
                    <img
                      v-if="appConfig.downloadQrImageUrls.harmonyos"
                      :src="appConfig.downloadQrImageUrls.harmonyos"
                      alt="鸿蒙应用商城下载二维码"
                      class="download-panel__qr-image"
                    />
                  </div>
                  <button
                    type="button"
                    class="store-button"
                    @click="openStore('harmonyos')"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M12 3v10m0 0 4-4m-4 4-4-4M5 19h14"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.8"
                      />
                    </svg>
                    <span>鸿蒙应用商城</span>
                  </button>
                </div>

                <div class="download-store">
                  <div class="download-panel__qr">
                    <img
                      v-if="appConfig.downloadQrImageUrls.android"
                      :src="appConfig.downloadQrImageUrls.android"
                      alt="Android 安装包下载二维码"
                      class="download-panel__qr-image"
                    />
                  </div>
                  <a
                    v-if="appConfig.downloadUrls.androidApk"
                    class="store-button"
                    :href="appConfig.downloadUrls.androidApk"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M12 3v10m0 0 4-4m-4 4-4-4M5 19h14"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.8"
                      />
                    </svg>
                    <span>Android 下载安装包</span>
                  </a>
                </div>

                <button
                  v-if="appConfig.downloadUrls.android"
                  type="button"
                  class="store-button store-button--apk"
                  @click="openStore('android')"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      d="M7 7h3.2v3.2H7V7Zm6.8 0H17v3.2h-3.2V7ZM7 13.8h3.2V17H7v-3.2Zm6.8 0H17V17h-3.2v-3.2Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>华为应用市场</span>
                </button>
              </div>

              <p class="download-panel__manual">
                已收到设备？
                <RouterLink to="/manual">查看电子说明书</RouterLink>
              </p>
            </div>
          </div>

          <div class="download-hero__visual">
            <ProductModelStage />
          </div>
        </div>
      </section>

      <section class="download-features">
        <div class="download-features__intro">
          <h2>为智能互动而生</h2>
          <p>每个细节都旨在让科技更有温度</p>
        </div>

        <div class="download-features__grid">
          <article
            v-for="feature in features"
            :key="feature.title"
            class="feature-card"
          >
            <div class="feature-card__icon" :data-icon="feature.icon">
              <svg v-if="feature.icon === 'star'" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="m12 4 2.3 4.66 5.14.75-3.72 3.62.88 5.12L12 16.9l-4.6 2.23.88-5.12-3.72-3.62 5.14-.75L12 4Z"
                  fill="none"
                  stroke="currentColor"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                />
              </svg>
              <svg v-else-if="feature.icon === 'chat'" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M7 8.5h10M7 12h6M6 18l2.4-2H18a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2Z"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                />
              </svg>
              <svg v-else-if="feature.icon === 'signal'" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M5 18v-3M9 18V9m4 9V5m4 13V11"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="1.8"
                />
              </svg>
              <svg v-else viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M13 2 5 13h6l-1 9 8-11h-6l1-9Z"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                />
              </svg>
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </article>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.download-page {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background: #ffffff;
}

.download-main {
  flex: 1;
}

.download-hero {
  background: #ffffff;
}

.download-hero__inner {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: clamp(32px, 5vw, 72px);
  align-items: center;
  width: min(100%, 1180px);
  margin: 0 auto;
  padding: clamp(40px, 6vw, 88px) clamp(20px, 5vw, 48px);
}

.download-headline {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  color: #000000;
  font-size: clamp(32px, 4.2vw, 48px);
  font-weight: 700;
  line-height: 1.15;
}

.download-headline__highlight {
  display: inline-block;
  width: fit-content;
  padding: 0 0.08em;
  background: linear-gradient(transparent 62%, #d4ff00 62%);
}

.download-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: clamp(28px, 4vw, 40px);
  padding: 28px 32px;
  border-radius: 20px;
  background: #f8ffd9;
}

.download-panel__header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.download-panel__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #000000;
  font-size: 15px;
  font-weight: 600;
}

.download-panel__label svg {
  width: 18px;
  height: 18px;
}

.download-panel__meta {
  margin: 0;
  color: rgba(0, 0, 0, 0.56);
  font-size: 14px;
  line-height: 1.6;
}

.download-panel__stores {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.download-store {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.download-panel__qr {
  width: 100%;
  max-width: 148px;
  padding: 12px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
}

.download-panel__qr-image {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
}

.store-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 180px;
  padding: 12px 16px;
  border: 0;
  border-radius: 12px;
  background: #000000;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.store-button:hover {
  opacity: 0.88;
}

.store-button svg {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.store-button span {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.store-button--apk {
  grid-column: 1 / -1;
  max-width: none;
  text-decoration: none;
}

.download-panel__manual {
  margin: 0;
  color: rgba(0, 0, 0, 0.72);
  font-size: 14px;
  line-height: 1.6;
}

.download-panel__manual a {
  color: #000000;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.download-panel__manual a:hover {
  color: rgba(0, 0, 0, 0.72);
}

.download-hero__visual {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.download-hero__visual :deep(.product-visual) {
  width: 100%;
  max-width: 560px;
  transform: none;
}

.download-features {
  background: #f7f7f5;
  padding: clamp(56px, 8vw, 96px) clamp(20px, 5vw, 48px);
}

.download-features__intro {
  width: min(100%, 1180px);
  margin: 0 auto 40px;
  text-align: center;
}

.download-features__intro h2 {
  margin: 0;
  color: #000000;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
}

.download-features__intro p {
  margin: 12px 0 0;
  color: rgba(0, 0, 0, 0.56);
  font-size: 15px;
}

.download-features__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  width: min(100%, 1180px);
  margin: 0 auto;
}

.feature-card {
  padding: 28px 24px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
}

.feature-card__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-bottom: 18px;
  border-radius: 12px;
  background: #d4ff00;
  color: #000000;
}

.feature-card__icon svg {
  width: 22px;
  height: 22px;
}

.feature-card h3 {
  margin: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 700;
}

.feature-card p {
  margin: 10px 0 0;
  color: rgba(0, 0, 0, 0.56);
  font-size: 14px;
  line-height: 1.7;
}

@media (max-width: 960px) {
  .download-hero__inner {
    grid-template-columns: 1fr;
  }

  .download-hero__visual {
    order: -1;
  }

  .download-hero__visual :deep(.product-visual) {
    max-width: 420px;
  }

  .download-features__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .download-panel {
    padding: 24px 20px;
  }

  .download-panel__stores {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .download-panel__qr {
    max-width: 128px;
  }

  .store-button {
    max-width: none;
    padding: 10px 12px;
  }

  .store-button span {
    font-size: 13px;
  }

  .download-features__grid {
    grid-template-columns: 1fr;
  }
}
</style>
